import { Dialog, DialogContent, Box, useMediaQuery, useTheme } from "@mui/material";
import { useState, useRef, useCallback } from "react";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import JobImportTable, { JobsImportTableProps } from ".";
import { MapContainer } from "./MapContainer";
import "./ImportTableWithMap.css";
import type { Job } from "../../../api/types";

interface ImportTableWithMapProps extends Omit<
	JobsImportTableProps,
	"setDrawerState" | "mapDisplayMode" | "setMapDisplayMode"
	// oxlint-disable-next-line no-empty-object-type
> {
	// Remove map control props since we'll handle them internally
}

export default function ImportTableWithMap(props: ImportTableWithMapProps) {
	const [mapDisplayMode, setMapDisplayMode] = useState<"modal" | "drawer" | null>(null);
	const [displayedJobs, setDisplayedJobs] = useState<Job[]>([]);
	const mapKeyRef = useRef(0);

	// Lasso selection state - lifted here so both table and map can access it
	const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());

	const handleSelectedFeaturesChange = useCallback((ids: Set<string>) => {
		setSelectedFeatureIds(ids);
	}, []);

	const handleClearFeatureSelection = useCallback(() => {
		setSelectedFeatureIds(new Set());
	}, []);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Handler to change map display mode - increments key to force remount
	const handleSetMapDisplayMode = (mode: "modal" | "drawer" | null) => {
		if (mode !== null) {
			mapKeyRef.current += 1;
		}
		setMapDisplayMode(mode);
	};

	const isMapVisible = mapDisplayMode !== null;

	return (
		<Box className="import-table-with-map">
			<PanelGroup direction="horizontal" autoSaveId="import-table-map-panels">
				{/* Table Panel - Always visible */}
				<Panel id="table-panel" order={1} defaultSize={mapDisplayMode === "drawer" ? 60 : 100} minSize={30}>
					<Box className="panel-content">
						<JobImportTable
							{...props}
							setDrawerState={() => {}} // No-op for backward compatibility
							setDisplayedJobs={setDisplayedJobs}
							drawerOpen={mapDisplayMode === "drawer"}
							mapDisplayMode={mapDisplayMode}
							setMapDisplayMode={handleSetMapDisplayMode}
							// Legacy props - derive from mapDisplayMode
							isMapVisible={isMapVisible}
							setIsMapVisible={(visible) => handleSetMapDisplayMode(visible ? "modal" : null)}
							// Lasso selection state for Split feature
							selectedFeatureIds={selectedFeatureIds}
							onClearFeatureSelection={handleClearFeatureSelection}
						/>
					</Box>
				</Panel>

				{/* Resize Handle + Map Panel - Conditional */}
				{mapDisplayMode === "drawer" && (
					<>
						<PanelResizeHandle className="resize-handle" />
						<Panel
							id="map-panel"
							order={2}
							defaultSize={40}
							minSize={20}
							onResize={() => window.dispatchEvent(new Event("resize"))}
						>
							<MapContainer
								jobs={displayedJobs}
								mapKey={mapKeyRef.current}
								onClose={() => setMapDisplayMode(null)}
								selectedFeatureIds={selectedFeatureIds}
								onSelectedFeaturesChange={handleSelectedFeaturesChange}
							/>
						</Panel>
					</>
				)}
			</PanelGroup>

			{/* Map modal */}
			<Dialog
				className="map-dialog"
				open={mapDisplayMode === "modal"}
				onClose={() => setMapDisplayMode(null)}
				maxWidth="xl"
				fullWidth
				fullScreen={isMobile}
			>
				<DialogContent>
					{mapDisplayMode === "modal" && (
						<MapContainer
							jobs={displayedJobs}
							mapKey={mapKeyRef.current}
							onClose={() => setMapDisplayMode(null)}
							selectedFeatureIds={selectedFeatureIds}
							onSelectedFeaturesChange={handleSelectedFeaturesChange}
						/>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
}
